import type { TemplateFormLanguage } from '../types/templateForms';
import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from './constants';

export interface FormValidationTranslations {
    fieldRequired: string;
    invalidDate: string;
    fillRequiredFields: string;
    invalidEmailSnackbar: string;
    /** Shown when "Other"/"Autre" is selected for lending institution */
    specifyLendingInstitution: string;
    differentIdTypes: string;
    valueCannotEqual: (fieldTitle: string) => string;
    /** Google Places: cleared input */
    selectAddressOrEnterManually: string;
    /** Google Places: invalid/required address fallback */
    validAddress: string;
    emailRequired: string;
    invalidEmail: string;
    numberRequired: string;
    fieldCannotBeEmpty: string;
    dateRequired: string;
    invalidValue: string;
    maximumLength: (length: number) => string;
    minimumLength: (length: number) => string;
    /** Tooltip on the critical-question indicator icon */
    criticalQuestionTooltip: string;
}

const ENGLISH_TRANSLATIONS: FormValidationTranslations = {
    fieldRequired: 'This field is required',
    invalidDate: 'Invalid Date',
    fillRequiredFields: 'Please fill all required fields.',
    invalidEmailSnackbar: 'Oops! That email format doesn’t seem right. Double-check and try again.',
    specifyLendingInstitution: 'Please specify your lending institution',
    differentIdTypes: 'Please select different ID types',
    valueCannotEqual: (fieldTitle) => `Value cannot be equal to '${fieldTitle}'`,
    selectAddressOrEnterManually:
        'Please select an address from the list or enter the address manually.',
    validAddress: 'Please enter a valid address',
    emailRequired: 'Email is required',
    invalidEmail: 'Invalid email address — must contain a valid domain and @ symbol',
    numberRequired: 'Number is required',
    fieldCannotBeEmpty: 'This field cannot be empty',
    dateRequired: 'Date is required',
    invalidValue: 'Invalid value',
    maximumLength: (length) => `Maximum length should be ${length} characters`,
    minimumLength: (length) => `Minimum length should be ${length} characters`,
    criticalQuestionTooltip:
        'This question is not required to complete the form today, but we will eventually require an answer to complete your deal.',
};

const FRENCH_TRANSLATIONS: FormValidationTranslations = {
    fieldRequired: 'Ce champ est obligatoire',
    invalidDate: 'Date invalide',
    fillRequiredFields: 'Veuillez remplir tous les champs obligatoires.',
    invalidEmailSnackbar:
        'Oups! Le format de ce courriel ne semble pas correct. Vérifiez-le et réessayez.',
    specifyLendingInstitution: 'Veuillez préciser votre institution de prêt',
    differentIdTypes: 'Veuillez sélectionner des types d’identification différents',
    valueCannotEqual: (fieldTitle) => `La valeur ne peut pas être identique à « ${fieldTitle} »`,
    selectAddressOrEnterManually:
        'Veuillez sélectionner une adresse dans la liste ou saisir l’adresse manuellement.',
    validAddress: 'Veuillez entrer une adresse valide',
    emailRequired: 'Le courriel est obligatoire',
    invalidEmail:
        'Adresse courriel invalide — elle doit contenir un domaine valide et le symbole @',
    numberRequired: 'Un nombre est obligatoire',
    fieldCannotBeEmpty: 'Ce champ ne peut pas être vide',
    dateRequired: 'La date est obligatoire',
    invalidValue: 'Valeur invalide',
    maximumLength: (length) => `La longueur maximale doit être de ${length} caractères`,
    minimumLength: (length) => `La longueur minimale doit être de ${length} caractères`,
    criticalQuestionTooltip:
        'Cette question n’est pas obligatoire pour remplir le formulaire aujourd’hui, mais une réponse sera éventuellement requise pour conclure votre dossier.',
};

export const FORM_VALIDATION_TRANSLATIONS: Record<
    TemplateFormLanguage,
    FormValidationTranslations
> = {
    en: ENGLISH_TRANSLATIONS,
    fr: FRENCH_TRANSLATIONS,
};

export function getFormValidationTranslations(
    language: TemplateFormLanguage = DEFAULT_TEMPLATE_FORM_LANGUAGE,
): FormValidationTranslations {
    return FORM_VALIDATION_TRANSLATIONS[language] ?? FORM_VALIDATION_TRANSLATIONS.en;
}
